import { createContext, useContext, useEffect, useMemo, useSyncExternalStore, type ReactNode } from 'react'
import type { Mesh } from 'three'

/**
 * Registro do que a cena pede ao pós-processamento.
 *
 * Só pode existir um composer por cena: dois montados no mesmo `<Canvas>` brigam
 * pelo quadro, e cada um retirava o tone mapping da cena inteira enquanto estava
 * montado. Quem quer um efeito não monta nada: registra o pedido aqui, e o
 * `PosProcessamento` decide o que desenhar.
 *
 * - contornos: malhas a destacar, em canais com cor própria;
 * - emissores: quantas fontes de luz forte (fogo, faíscas) estão ativas, que é
 *   quando o brilho tem o que mostrar.
 */
export type CanalContorno = 'selecao' | 'deformadas'

export type MalhasPorCanal = Readonly<Record<CanalContorno, readonly Mesh[]>>

export interface PedidosPosProcessamento {
  readonly contornos: MalhasPorCanal
  readonly emissores: number
}

const SEM_MALHAS: readonly Mesh[] = []
const VAZIO: PedidosPosProcessamento = {
  contornos: { selecao: SEM_MALHAS, deformadas: SEM_MALHAS },
  emissores: 0,
}

export interface RegistroPosProcessamento {
  publicarContorno(canal: CanalContorno, malhas: readonly Mesh[]): void
  /** Declara um emissor ativo; devolve a função que o retira. */
  registrarEmissor(): () => void
  /** Instantâneo imutável: só muda de identidade quando algo muda. */
  ler(): PedidosPosProcessamento
  assinar(aviso: () => void): () => void
}

function mesmasMalhas(a: readonly Mesh[], b: readonly Mesh[]): boolean {
  return a.length === b.length && a.every((malha, i) => malha === b[i])
}

export function criarRegistroPosProcessamento(): RegistroPosProcessamento {
  let atual: PedidosPosProcessamento = VAZIO
  const ouvintes = new Set<() => void>()
  const avisar = () => ouvintes.forEach((aviso) => aviso())

  return {
    publicarContorno(canal, malhas) {
      const novas = malhas.length === 0 ? SEM_MALHAS : malhas
      if (mesmasMalhas(atual.contornos[canal], novas)) return
      atual = { ...atual, contornos: { ...atual.contornos, [canal]: novas } }
      avisar()
    },
    registrarEmissor() {
      atual = { ...atual, emissores: atual.emissores + 1 }
      avisar()
      let retirado = false
      return () => {
        if (retirado) return
        retirado = true
        atual = { ...atual, emissores: atual.emissores - 1 }
        avisar()
      }
    },
    ler: () => atual,
    assinar(aviso) {
      ouvintes.add(aviso)
      return () => void ouvintes.delete(aviso)
    },
  }
}

/** Sem provedor (a tela de projeção, por exemplo) não há contorno nem emissor registrado. */
const REGISTRO_VAZIO: RegistroPosProcessamento = {
  publicarContorno: () => {},
  registrarEmissor: () => () => {},
  ler: () => VAZIO,
  assinar: () => () => {},
}

const ContextoRegistro = createContext<RegistroPosProcessamento>(REGISTRO_VAZIO)

export function ProvedorPosProcessamento({ children }: { children: ReactNode }) {
  const registro = useMemo(criarRegistroPosProcessamento, [])
  return <ContextoRegistro.Provider value={registro}>{children}</ContextoRegistro.Provider>
}

/** O que está pedido hoje; re-renderiza quando muda. */
export function usePedidosPosProcessamento(): PedidosPosProcessamento {
  const registro = useContext(ContextoRegistro)
  return useSyncExternalStore(registro.assinar, registro.ler, registro.ler)
}

/**
 * Registra as malhas a contornar num canal e as retira ao desmontar ou quando a
 * lista muda. Passar lista vazia é o jeito de apagar o contorno.
 */
export function useContorno(canal: CanalContorno, malhas: readonly Mesh[]): void {
  const registro = useContext(ContextoRegistro)

  useEffect(() => {
    registro.publicarContorno(canal, malhas)
    return () => registro.publicarContorno(canal, SEM_MALHAS)
  }, [registro, canal, malhas])
}

/** Declara que este componente é uma fonte de luz forte enquanto `ativo`. */
export function useEmissor(ativo: boolean): void {
  const registro = useContext(ContextoRegistro)

  useEffect(() => {
    if (!ativo) return
    return registro.registrarEmissor()
  }, [registro, ativo])
}
